/**  ────────────────────────────────────────────────────────────────
API client — the one place every backend call goes through.

Responsibilities:
  1. Prepend nothing to the path; we use relative /api/... URLs
     because the Vite proxy (dev) and FastAPI static serve (prod)
     both make /api same-origin.
  2. Attach Bearer token from sessionStorage (mirrors the existing
     HTML dashboard's auth scheme, so a token deposited by MSAL
     flows through without extra plumbing).
  3. Parse JSON responses.
  4. Throw a typed ApiError on non-2xx so callers can branch on
     .status without re-parsing the response.
────────────────────────────────────────────────────────────────
*/
import { SESSION_KEY } from "../auth/sessionKey";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`API ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

function authHeader(): Record<string, string> {
  const token = sessionStorage.getItem(SESSION_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Coalesce concurrent refreshes: if ten queries fire at once and all 401,
// we want ONE re-exchange, not ten parallel MSAL token requests.
let refreshInflight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const { refreshSession } = await import("../auth/runAuthFlow");
      return await refreshSession();
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = () =>
    fetch(path, {
      ...init,
      headers: {
        Accept: "application/json",
        ...authHeader(),
        ...(init?.headers ?? {}),
      },
    });

  let res = await doFetch();

  if (res.status === 401 && !path.startsWith("/api/v1/auth/")) {
    const ok = await refreshOnce();
    if (ok) res = await doFetch();
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
    }
    throw new ApiError(res.status, res.statusText, body);
  }

  return (await res.json()) as T;
}

/**
 * Fetch a binary response (e.g. a PDF) with the same auth and 401-refresh
 * behaviour as apiFetch.
 *
 * A plain `<a href>` cannot be used for these: browser navigation sends
 * cookies but not the Authorization header, and this API authenticates by
 * bearer token held in sessionStorage — so the request arrives unauthenticated
 * and the user sees {"detail":"Missing Bearer token"} instead of a download.
 */
export async function apiFetchBlob(path: string, init?: RequestInit): Promise<Blob> {
  const doFetch = () =>
    fetch(path, {
      ...init,
      headers: {
        ...authHeader(),
        ...(init?.headers ?? {}),
      },
    });

  let res = await doFetch();

  if (res.status === 401 && !path.startsWith("/api/v1/auth/")) {
    const ok = await refreshOnce();
    if (ok) res = await doFetch();
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body — status alone has to carry the meaning */
    }
    throw new ApiError(res.status, res.statusText, body);
  }

  return await res.blob();
}
